import dynamic from "next/dynamic"
import { useRouter } from "next/router"
import { MainLayout } from "components/Layout"
import { useLang } from "hooks/useLang"
import { EventJsonLd, NextSeo, BreadcrumbJsonLd } from "next-seo"
import React, { useState, useEffect } from "react"
import { IFinalEvent } from "types/event"
import useWindowSize from "hooks/useWindowSize"
import removeMarkdown from "utils/helpers/removeMarkdown"
import * as gtag from "../../../lib/gtag"
import PixelIntegrationComponent from "components/Analytics/PixelIntegrationComponent"
import { useQuery, QueryClient, dehydrate } from "@tanstack/react-query"
import { ITicket } from "types/ticket"
import { useCartDispatch } from "hooks/useCartV2"
import { ImprovedCartActionsEnum } from "types/improved-booking"
import { fetchEvent } from "api"
import { useAnalytics } from "components/Analytics/UnifiedAnalyticsProvider"
import { queryKeys } from "utils/queryKeys"
import axios from "axios"
import { useMutation } from "@tanstack/react-query"
import PrivateEventModal from "components/Modal/PrivateEventModal"
import BlurView from "../../../components/EventDetailsPage/components/blurview"
import EventLayout from "../../../components/EventDetailsPage/components/layout"
import Image from "next/image"
import dayjs from "dayjs"
import LocationIcon from "components/icons/LocationIcon"
import DateIcon from "components/icons/DateIcon"
import EventDescription from "components/EventDetailsPage/EventDescription"
import EventGallery from "components/EventDetailsPage/EventGallery"

import BuyBtn from "../../../components/EventDetailsPage/components/buyBtn"
import ShareAndLike from "components/EventDetailsPage/ShareAndLike"
import EventSkeleton from "components/ActivityLoader/activityLoader"
import RenderVideo from "components/video/renderVideo"
import EventTerms from "components/bookingv2/EventTerms"
import CustomMarkdown from "components/atoms/CustomMarkdown"
import AuthModal from "components/bookingv2/AuthModal/AuthModal"
import Link from "next/link"

const LiveMap = dynamic(() => import("../../../components/LiveMap/liveMap"), {
    ssr: false,
})

export const meta = {
    author: "StartHubIT",
}

export async function getServerSideProps(context) {
    const queryClient = new QueryClient()

    const rawEventId = context.params?.eventid as string
    const pathToEventIdMap: { [key: string]: string } = {
        "3x3jeddah": "6564a239d57a803c355ea0b0",
        f1: process.env.NEXT_PUBLIC_CURRENT_F1_ID!,
        f: process.env.NEXT_PUBLIC_CURRENT_F1_ID!,
    }

    const eventId = pathToEventIdMap[rawEventId] || rawEventId

    try {
        await queryClient.prefetchQuery({
            queryKey: queryKeys.event(eventId),
            queryFn: () => fetchEvent(eventId),
        })
    } catch (error) {
        console.error("Error prefetching event data on server:", error)
    }

    return {
        props: {
            dehydratedState: dehydrate(queryClient),
        },
    }
}

export type FetchEventResponse = {
    message: string
    payload: {
        event: IFinalEvent | Event
        tickets: ITicket[]
    }
}

export default function NewEventDetailsPage() {
    const { isDesktop } = useWindowSize()
    const { t } = useLang()
    const { isAr } = useLang()
    const { query, push } = useRouter()
    const router = useRouter()
    const [showBuy, setShowBuy] = useState(false)
    const [showAuthModal, setShowAuthModal] = useState(false)

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 200) {
                setShowBuy(true)
            } else {
                setShowBuy(false)
            }
        }

        window.addEventListener("scroll", handleScroll)
        return () => window.removeEventListener("scroll", handleScroll)
    }, [])

    const [openPrivateEventModal, setOpenPrivateEventModal] =
        useState<boolean>(false)
    // const [enteredCode, setEnteredCode] = useState("");
    const [errorMessage, setErrorMessage] = useState("")
    const [isAuthorized, setIsAuthorized] = useState(false)

    const cartDispatch = useCartDispatch()
    //TODO: refactor - match the mapping in booking page
    const pathToEventIdMap: { [key: string]: string } = {
        "3x3jeddah": "6564a239d57a803c355ea0b0",
        // guvali: process.env.NEXT_PUBLIC_CURRENT_GUVALI_ID!,
        f1: process.env.NEXT_PUBLIC_CURRENT_F1_ID!,
        f: process.env.NEXT_PUBLIC_CURRENT_F1_ID!,
    }

    // Use the event ID from the URL (with mapping) or the one from SSG props
    const eventId =
        pathToEventIdMap[router?.query?.eventid as string] ||
        (router?.query?.eventid as string)
    //END TODO

    // const queryClient = useQueryClient()
    // Get cached event data if available
    // const cachedEventData = queryCl/ient.getQueryData<{ event: IFinalEvent; tickets: ITicket[] }>(queryKeys.event(eventId))

    const { data, error, isLoading } = useQuery({
        queryKey: queryKeys.event(eventId),
        queryFn: () => fetchEvent(eventId),
        // initialData: cachedEventData ?? { event, tickets },
        staleTime: 300000, // 5 minutes (Updated from 60000)
        gcTime: 600000, // 10 minutes
        refetchOnWindowFocus: true,
        retry: (failureCount, error) => {
            return error.message !== "EVENT_NOT_FOUND" && failureCount < 3
        },
    })

    const event = data?.payload?.event as IFinalEvent

    useEffect(() => {
        if (!router.isReady) return
        if (!event?.slug) return

        const currentParam = router.query.eventid as string | undefined
        if (!currentParam || currentParam === event.slug) return

        const [pathWithoutHash] = router.asPath.split("#")
        const [pathWithoutQuery, queryString] = pathWithoutHash.split("?")
        const segments = pathWithoutQuery.split("/")

        if (segments.length === 0) return

        segments[segments.length - 1] = event.slug
        const newPath = segments.join("/")
        const finalPath = queryString ? `${newPath}?${queryString}` : newPath

        router.replace(finalPath, undefined, { shallow: true })
    }, [
        event?.slug,
        router.isReady,
        router.query.eventid,
        router.asPath,
        router,
    ])

    const pageVisitSource = router.query.source as string

    const { trackEvent } = useAnalytics()

    const handleAnalyticsEvent = () => {
        trackEvent(
            "source_referral",
            {
                category: "Referral",
                label: "source",
                value: pageVisitSource || "unknown",
            },
            "event",
            event._id
        )
    }

    useEffect(() => {
        if (error instanceof Error && error.message === "EVENT_NOT_FOUND") {
            router.push("/404")
        }
    }, [error, router])

    useEffect(() => {
        if (pageVisitSource) {
            handleAnalyticsEvent()
        }
    }, [pageVisitSource])

    useEffect(() => {
        return () => {
            cartDispatch({ type: ImprovedCartActionsEnum.CLEAR_CART })
        }
    }, [cartDispatch])

    useEffect(() => {
        if (event?.is_private) {
            setOpenPrivateEventModal(true)
        }
    }, [event])

    const updatedEvent = data?.payload?.event as IFinalEvent

    const getDesktopPoster = (ev: IFinalEvent) => {
        const posterPath = ev?.media?.posters?.web_poster?.original || ""
        const defaultImageUrl = "/svg/v2/pass_ticket_fallback_image_light.png"
        return posterPath
            ? `${process.env.NEXT_PUBLIC_Event_Img_URL}${posterPath}`
            : defaultImageUrl
    }

    const getMobilePoster = (ev: IFinalEvent) => {
        const posterPath = ev?.media?.posters?.mobile_poster?.original || ""
        const defaultImageUrl = "/svg/v2/pass_ticket_fallback_image_light.png"
        return posterPath
            ? `${process.env.NEXT_PUBLIC_Event_Img_URL}${posterPath}`
            : defaultImageUrl
    }

    const bgURLDesktop = getDesktopPoster(updatedEvent)
    const bgURLMobile = getMobilePoster(updatedEvent)

    // const isExpiredEvent = (): boolean => {
    //   const currentDate = new Date();
    //   const eventEndingDate = new Date(
    //     updatedEvent?.event_date?.ending_date || ""
    //   );
    //   if (currentDate < eventEndingDate) {
    //     return true;
    //   } else if (currentDate > eventEndingDate) {
    //     return false;
    //   }
    //   return false;
    // };
    // const isExpired = isExpiredEvent();

    const pageTitle = `${
        isAr ? updatedEvent?.ar_title || "" : updatedEvent?.en_title || ""
    }`
    // Create plain event title without site name
    const eventTitle = isAr ? updatedEvent?.ar_title : updatedEvent?.en_title

    const description = isAr
        ? updatedEvent?.ar_description
        : updatedEvent?.en_description || eventTitle
    const cleanDescription = removeMarkdown(description)
    const pageDescription = cleanDescription.slice(0, 160)
    const shortDescription = cleanDescription.slice(0, 100)
    const baseUrl = (
        process.env.NEXT_PUBLIC_SITE_URL || "https://pass-tickets.com"
    ).replace(/\/$/, "")
    const currentPath = router.asPath.split("#")[0].split("?")[0] || "/"
    const canonicalUrl = `${baseUrl}${currentPath}`
    const imageUrl = getDesktopPoster(updatedEvent)
    const localePrefix =
        currentPath.match(/^\/(en-US|ar-SA)(?=\/|$)/)?.[0] || ""

    const formatDateISO = (dateString: string) => {
        if (!dateString) return ""
        const date = new Date(dateString)
        return date.toISOString()
    }

    const startDate = formatDateISO(
        updatedEvent?.event_date?.starting_date.toString()
    )
    const endDate = formatDateISO(
        updatedEvent?.event_date?.ending_date.toString()
    )

    const keywords = `${updatedEvent?.en_title}, ${updatedEvent?.ar_title}, 
    tickets, events, Saudi Arabia, ${isAr ? "باس تيكيتس" : "PassTickets"}`

    const verifyCodeMutation = useMutation({
        mutationFn: async (code: string) => {
            const res = await axios.post(
                `${process.env.NEXT_PUBLIC_API_URL}coupons/verify`,
                {
                    event_id: updatedEvent?._id,
                    coupon_code: code,
                }
            )
            return res.data
        },
        onSuccess: (data) => {
            if (data.payload) {
                setIsAuthorized(true)
                setOpenPrivateEventModal(false)
                setErrorMessage("")
            } else {
                setErrorMessage("❌ Invalid code, please try again.")
            }
        },
        onError: () => {
            setErrorMessage("⚠️ Something went wrong. Try again later.")
        },
    })

    // const handleVerifyCode = () => {
    //   if (!enteredCode) {
    //     setErrorMessage("Code is required.");
    //     return;
    //   }
    //   verifyCodeMutation.mutate(enteredCode);
    // };

    if (query.shared) {
        gtag.event({
            action: "view_item",
            category: "event",
            label: `share_event_${updatedEvent?._id}`,
            value: `${query.shared}`,
        })
    }

    if (error) {
        console.error("Error loading event data:", error)
        return (
            <MainLayout>
                <div className="flex flex-col items-center justify-center h-screen">
                    <h2 className="text-xl mb-4">Failed to load event data</h2>
                    <button
                        className="bg-mint-green-500 hover:bg-mint-green-600 text-white px-6 py-2 rounded-full"
                        onClick={() => window.location.reload()}
                    >
                        Retry
                    </button>
                </div>
            </MainLayout>
        )
    }

    // // ✅ If event is private & user not authorized, block content
    if (event?.is_private && !isAuthorized) {
        return (
            <PrivateEventModal
                open={event?.is_private && !isAuthorized}
                onClose={() => push("/")}
                onVerify={(code) => verifyCodeMutation.mutate(code)}
                isLoading={verifyCodeMutation.isPending}
                errorMessage={errorMessage}
            />
        )
    }


    if (isLoading) {
        return <EventSkeleton />
    }

    const eventDate = event?.upcoming_time ?? event?.event_date?.starting_date
    return (
        <>
            <NextSeo
                title={pageTitle}
                description={pageDescription}
                canonical={canonicalUrl}
                languageAlternates={(() => {
                    const pathNoLocale = currentPath.replace(
                        /^\/(en-US|ar-SA)(?=\/|$)/,
                        ""
                    )
                    const defaultHref = `${baseUrl}${pathNoLocale || "/"}`
                    const enHref = `${baseUrl}/en-US${pathNoLocale || "/"}`
                    return [
                        { hrefLang: "x-default", href: defaultHref },
                        { hrefLang: "ar", href: defaultHref },
                        { hrefLang: "en", href: enHref },
                    ]
                })()}
                openGraph={{
                    type: "website",
                    locale: isAr ? "ar_SA" : "en_US",
                    url: canonicalUrl,
                    title: eventTitle,
                    description: pageDescription,
                    site_name: isAr ? "باس تيكيتس" : "PassTickets",
                    images: [
                        {
                            url: imageUrl,
                            width: 1200,
                            height: 630,
                            alt: eventTitle,
                            type: "image/jpeg",
                        },
                    ],
                }}
                twitter={{
                    handle: "@passtickets",
                    site: "@passtickets",
                    cardType: "summary_large_image",
                }}
                additionalMetaTags={[
                    {
                        name: "keywords",
                        content: keywords,
                    },
                    {
                        name: "author",
                        content: isAr ? "باس تيكيتس" : "PassTickets",
                    },
                ]}
            />

            {/* Breadcrumb JSON-LD */}
            <BreadcrumbJsonLd
                useAppDir={false}
                itemListElements={[
                    {
                        position: 1,
                        name: isAr ? "الصفحة الرئيسية" : "Home",
                        item: `${baseUrl}${localePrefix || "/"}`,
                    },
                    {
                        position: 2,
                        name: isAr ? "الفعاليات" : "Events",
                        item: `${baseUrl}${localePrefix}/events`,
                    },
                    {
                        position: 3,
                        name: eventTitle || pageTitle,
                        item: canonicalUrl,
                    },
                ]}
            />

            {/* Event JSON-LD Structured Data */}
            <EventJsonLd
                useAppDir={false}
                url={canonicalUrl}
                name={eventTitle || ""}
                description={shortDescription}
                images={[imageUrl]}
                startDate={startDate}
                endDate={endDate}
                location={{
                    name: updatedEvent?.organisation?.en_name || "Saudi Arabia",
                    address: {
                        addressLocality:
                            updatedEvent?.address?.city || "Saudi Arabia",
                    },
                }}
                offers={{
                    url: canonicalUrl,
                    priceCurrency: "SAR",
                    availability:
                        new Date() <
                        new Date(updatedEvent?.event_date?.ending_date || "")
                            ? "https://schema.org/InStock"
                            : "https://schema.org/SoldOut",
                }}
                organizer={{
                    name: isAr
                        ? event.organisation.ar_name
                        : event.organisation.en_name,
                    url: baseUrl,
                }}
            />

            <PixelIntegrationComponent>
                <BlurView img={isDesktop ? bgURLDesktop : bgURLMobile} />
                <EventLayout logo={event?.organisation?.logo}>
                    <div className="min-h-screen layout  md:max-w-[1220px] ">
                        <div className="flex flex-col md:flex-row max-w-7xl mx-auto  gap-10">
                            {/* Left Sidebar */}
                            <aside className="hidden md:flex flex-col w-full md:w-1/3 lg:w-[30%] sticky top-24 self-start  gap-4 pb-4">
                                {/* Poster Image */}
                                <div className="relative w-full  mx-auto flex-1 min-h-0 aspect-[11/16] rounded-[15px] overflow-hidden">
                                    <Image
                                        src={bgURLMobile}
                                        fill
                                        alt=""
                                        className="object-cover aspect-[11/16] bg-red-50 "
                                    />
                                </div>

                                {/* Buttons Container */}
                                <div className="flex flex-col gap-2">
                                    <BuyBtn event={event} />
                                    <div className="w-full flex flex-row gap-2">
                                        <ShareAndLike
                                            setShowAuthModal={setShowAuthModal}
                                            showTitles
                                            event={event}
                                            iconsSize={20}
                                        />
                                    </div>
                                </div>
                            </aside>

                            {/* Right Main Content */}
                            <section className="w-full md:w-2/3 space-y-6  mt-24 lg:w-[70%] ">
                                <section className="flex flex-col gap-10 lg:gap-20 ">
                                    <div
                                        className={`w-full h-[1px] bg-[#FFFFFF80] hidden md:flex`}
                                    />

                                    <div className="flex flex-col gap-4">
                                        <div className="flex flex-col">
                                            <div className="w-full  aspect-[11/16] md:hidden relative">
                                                <Image
                                                    src={bgURLMobile}
                                                    fill
                                                    alt=""
                                                    className="  aspect-[11/16] object-cover rounded-[15px]"
                                                />
                                            </div>
                                            <div className="w-full flex flex-row  gap-2 md:hidden my-[14px] justify-end relative">
                                                <ShareAndLike
                                                    setShowAuthModal={
                                                        setShowAuthModal
                                                    }
                                                    event={event}
                                                    iconsSize={25}
                                                />
                                            </div>
                                            <Link
                                                href={`/organizer/${event?.organisation?._id}`}
                                            >
                                                <span className="text-[#D9D9D9] text-[20px] md:text-[20px] underline">
                                                    {isAr
                                                        ? event?.organisation
                                                              ?.ar_name
                                                        : event?.organisation
                                                              ?.en_name}
                                                </span>
                                            </Link>
                                            <span className="font-bold text-white text-[28px] md:text-[49px]">
                                                {eventTitle}
                                            </span>
                                            <span className=" text-white md:text-[20px] text-[14px]">
                                                {isAr
                                                    ? event?.ar_description
                                                    : event?.en_description}
                                            </span>
                                        </div>

                                        <div className="flex-col rounded-lg flex  gap-2 ">
                                            {/* <div className={`w-full h-[1px] bg-[#FFFFFF80]`} /> */}
                                            <div
                                                onClick={() =>
                                                    window.open(
                                                        `https://maps.google.com/?q=${event?.address?.location?.lat},${event?.address?.location?.long}`,
                                                        "_blank" // opens in a new tab
                                                    )
                                                }
                                                className="flex flex-row gap-1.5 items-center cursor-pointer hover:underline w-fit"
                                            >
                                                <div className="bg-[#00000042] rounded-md p-2">
                                                    <LocationIcon />
                                                </div>

                                                <span className="text-[#83EDBF] md:text-[16px] text-[14px] capitalize">
                                                    {t(event?.address?.city)},{" "}
                                                    {t(event?.address?.country)}
                                                </span>
                                            </div>

                                            <div className="flex flex-row gap-1.5 items-center">
                                                <div className="bg-[#00000042]  rounded-md  p-2">
                                                    <DateIcon className="fill-secondary-theme-200 dark:fill-[#83EDBF]" />
                                                </div>
                                                <span className=" text-[#83EDBF] md:text-[16px] text-[14px]">{`${t(dayjs(eventDate).format("dddd"))}, ${t(dayjs(eventDate).format("MMMM"))} ${dayjs(eventDate).format("DD")} ${t("at")} ${dayjs(eventDate).format("hh")}:${dayjs(eventDate).format("mm")} ${t(dayjs(eventDate).format("A"))}`}</span>
                                            </div>

                                            {/** Keywords */}

                                            {event?.keywords &&
                                                event?.keywords.length > 0 && (
                                                    <div className="flex flex-row mt-4 gap-2">
                                                        {event?.keywords.map(
                                                            (keyword) => (
                                                                <span
                                                                    key={
                                                                        keyword
                                                                    }
                                                                    className="px-6  h-[25px] bg-[#485e7bc6] rounded-full flex items-center justify-center text-[14px] border-[1px] border-[#fffs]"
                                                                >
                                                                    {keyword}
                                                                </span>
                                                            )
                                                        )}
                                                    </div>
                                                )}
                                        </div>
                                    </div>

                                    <div
                                        className={`w-full h-[1px] bg-[#FFFFFF80]`}
                                    />
                                </section>

                                <section className="flex flex-col gap-10">
                                    {!event?.details_sections?.length && (
                                        <div className="lg:w-[100%] flex-col flex gap-2">
                                            <span className="font-bold text-[#fff] text-[28px]">
                                                {t("description")}
                                            </span>
                                            <EventDescription event={event} />
                                        </div>
                                    )}

                                    {event?.details_sections?.length &&
                                        event?.details_sections?.map(
                                            (item, key) => {
                                                return (
                                                    <div
                                                        className="lg:w-[100%] flex-col flex gap-2"
                                                        key={key}
                                                    >
                                                        <span className="font-bold text-[#fff] text-[28px]">
                                                            {isAr
                                                                ? item?.ar_title
                                                                : item?.en_title}
                                                        </span>
                                                        <div
                                                            className="mb-6 last:mb-0"
                                                            id="event-description"
                                                            key={key}
                                                        >
                                                            <CustomMarkdown
                                                                title={""}
                                                                content={
                                                                    isAr
                                                                        ? item?.ar_content
                                                                        : item?.en_content ||
                                                                          ""
                                                                }
                                                                className="text-white text-[14px] md:text-[20px] leading-relaxed"
                                                            />
                                                        </div>
                                                    </div>
                                                )
                                            }
                                        )}

                                    {updatedEvent?.media?.posters?.gallery &&
                                        updatedEvent.media.posters.gallery
                                            .length > 0 && (
                                            <div className="lg:w-[100%] flex-col flex gap-2">
                                                <span className="font-bold text-[#fff] text-[28px]">
                                                    {t("Gallery")}
                                                </span>
                                                <EventGallery event={event} />
                                            </div>
                                        )}

                                    {event?.videos?.length !== 0 && (
                                        <div className="lg:w-[100%] flex-col flex gap-2">
                                            <span className="font-bold text-[#fff] text-[28px] capitalize">
                                                {t("videos")}
                                            </span>
                                            <RenderVideo
                                                url={event?.videos[0]}
                                                controls={true}
                                            />
                                        </div>
                                    )}

                                    {!isLoading && (
                                        <div className="lg:w-[100%] flex-col flex gap-2">
                                            <span className="font-bold text-[#fff] text-[28px] capitalize">
                                                {t("location")}
                                            </span>
                                            <LiveMap
                                                title={eventTitle}
                                                longitude={
                                                    event?.address?.location
                                                        ?.long
                                                }
                                                latitude={
                                                    event?.address?.location
                                                        ?.lat
                                                }
                                            />
                                            <div>
                                                <span
                                                    className="underline cursor-pointer text-3 hover:opacity-50"
                                                    onClick={() =>
                                                        window.open(
                                                            `https://maps.google.com/?q=${event?.address?.location?.lat},${event?.address?.location?.long}`,
                                                            "_blank" // opens in a new tab
                                                        )
                                                    }
                                                >
                                                    {t("get_the_directions")}
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    <div className="lg:w-[100%] flex-col flex gap-2">
                                        <span className="font-bold text-[#fff] text-[28px]">
                                            {t("USER_TAC_TITLE")}
                                        </span>
                                        <EventTerms event={event} />
                                    </div>

                                    <div className="flex md:hidden pb-4" />
                                </section>
                            </section>
                        </div>
                        <div className="hidden md:flex w-full relative  mt-20 mb-20 flex flex-col gap-4">
                            <div className="w-full lg:max-w-[1230px] aspect-video relative">
                                <Image
                                    src={bgURLDesktop}
                                    fill
                                    alt=""
                                    className=" aspect-video object-cover rounded-[15px]"
                                />
                            </div>

                            <BuyBtn event={event} />
                        </div>
                    </div>


                    <div
                        className={`w-full h-[100px] fixed z-50 bottom-0 left-0 right-0 flex items-center justify-center md:hidden pointer-events-none transition-transform duration-500 ${
                            showBuy ? "translate-y-0" : "translate-y-full"
                        }`}
                    >
                        <div className="w-[90%] h-full pointer-events-auto">
                            <BuyBtn event={event} />
                        </div>
                    </div>
                </EventLayout>
            </PixelIntegrationComponent>
            {showAuthModal && (
                <AuthModal
                    onClose={() => setShowAuthModal((prev) => !prev)}
                    onLoginSuccess={() => false}
                    onSignupSuccess={() => false}
                />
            )}
        </>
    )
}
